import { addMonths, format } from 'date-fns';
import { orderBy } from 'lodash';
import store from 'store2';

import { JSONType } from '@skippay/core/@types/primitives';
import { isJsonObject } from '@skippay/core/utils/objectUtils';

import {
	INSURANCE_CONTACT_TYPES,
	InsuranceContactType,
	InsuranceOfferMemory,
	MONTH_KEY_FORMAT,
} from './types';

export const INSURANCE_OFFER_MEMORY_KEY = '__jarabot_insurance_offer_memory';

const RETENTION_MONTHS = 24;

export const MAX_CONTACTED_ENTRIES = 500;

export const createEmptyInsuranceOfferMemory = (): InsuranceOfferMemory => ({
	contacted: {},
	lastMonthlyMessageMonth: null,
});

function toContacted(value: JSONType): Record<string, string> {
	if (!isJsonObject(value)) return {};
	const entries = Object.entries(value).filter(
		(entry): entry is [string, string] => typeof entry[1] === 'string'
	);
	return Object.fromEntries(entries);
}

function toMemory(value: JSONType): InsuranceOfferMemory {
	if (!isJsonObject(value)) return createEmptyInsuranceOfferMemory();
	const month = value.lastMonthlyMessageMonth;
	return {
		contacted: toContacted(value.contacted),
		lastMonthlyMessageMonth: typeof month === 'string' ? month : null,
	};
}

function prune(memory: InsuranceOfferMemory, now: Date): InsuranceOfferMemory {
	const cutoff = addMonths(now, -RETENTION_MONTHS).getTime();
	const contacted = Object.entries(memory.contacted).filter(([, contactedAt]) => {
		const time = new Date(contactedAt).getTime();
		return !Number.isNaN(time) && time >= cutoff;
	});

	const capped =
		contacted.length > MAX_CONTACTED_ENTRIES
			? orderBy(contacted, ([, contactedAt]) => contactedAt, 'desc').slice(0, MAX_CONTACTED_ENTRIES)
			: contacted;

	return { ...memory, contacted: Object.fromEntries(capped) };
}

export function readInsuranceOfferMemory(customerId: string, now: Date): InsuranceOfferMemory {
	let parsed: JSONType = null;
	try {
		parsed = store.local.get(INSURANCE_OFFER_MEMORY_KEY);

		if (isJsonObject(parsed) && parsed.customerId !== customerId) {
			store.local.remove(INSURANCE_OFFER_MEMORY_KEY);
			return createEmptyInsuranceOfferMemory();
		}
	} catch {
		return createEmptyInsuranceOfferMemory();
	}
	return prune(toMemory(parsed), now);
}

interface SaveInsuranceOfferContactParams {
	memory: InsuranceOfferMemory;
	customerId: string;
	offerIds: string[];
	contactType: InsuranceContactType;
	now: Date;
}

export function saveInsuranceOfferContact({
	memory,
	customerId,
	offerIds,
	contactType,
	now,
}: SaveInsuranceOfferContactParams): InsuranceOfferMemory {
	const contacted = { ...memory.contacted };
	const contactedAt = now.toISOString();

	offerIds.forEach((offerId) => {
		if (!(offerId in contacted)) contacted[offerId] = contactedAt;
	});

	const isMonthlyMessage = contactType !== INSURANCE_CONTACT_TYPES.FIRST_CONTACT;
	const saved: InsuranceOfferMemory = {
		contacted,
		lastMonthlyMessageMonth: isMonthlyMessage
			? format(now, MONTH_KEY_FORMAT)
			: memory.lastMonthlyMessageMonth,
	};

	try {
		store.local.set(INSURANCE_OFFER_MEMORY_KEY, { customerId, ...saved });
	} catch {
		// localStorage not available
	}

	return saved;
}
